// The account host's /console/v1/ answers (platform/controlplane/internal/
// console/console.go). Times are RFC 3339 strings; every list carries the
// server's clock as `now`, so countdowns do not trust the browser's.

export type SessionState =
  | { state: 'signed_out'; csrf: string; now: string }
  | { state: 'code'; csrf: string; email: string; expires_at: string; resend_at: string; sends: number; now: string }
  | { state: 'signed_in'; csrf: string; account: string; expires_at: string; now: string }

export type ShareState = 'ready' | 'reconnecting' | 'starting'

export interface Person {
  email: string
  invite?: 'sent' | 'not_sent'
  opened_at?: string
}

export interface Share {
  id: string
  label: string
  link: string
  apps: string[]
  state: ShareState
  device: { id: string; name: string }
  people: Person[]
  created_at: string
  expires_at: string
}

export interface Device {
  id: string
  name: string
  signed_in_at: string
  last_used_at?: string
  shares: number
}

export interface Browser {
  id: string
  name: string
  signed_in_at: string
  last_used_at?: string
  current: boolean
}

// The leaderboard (ADR-0029): every region with its coarse share of the
// votes, a multiple of 5, never a count, and whether this account voted for
// it. US East, where Purlview runs, is not among them.
export interface Region {
  id: string
  name: string
  share: number
  voted: boolean
}

export interface RegionList {
  regions: Region[]
  now: string
}

export interface ShareList {
  shares: Share[]
  now: string
}
export interface DeviceList {
  devices: Device[]
  now: string
}
export interface BrowserList {
  browsers: Browser[]
  now: string
}

// The error codes the console answers with; the console owns the words.
export type ErrorCode =
  | 'forbidden'
  | 'bad_request'
  | 'not_found'
  | 'signed_out'
  | 'invalid_email'
  | 'code_length'
  | 'wrong_code'
  | 'expired'
  | 'too_many_codes'
  | 'no_more_codes'
  | 'wait'
  | 'unavailable'
  | 'network'

export class ConsoleError extends Error {
  readonly code: ErrorCode
  readonly status: number
  readonly attemptsLeft: number
  readonly email: string
  constructor(code: ErrorCode, status: number, attemptsLeft = 0, email = '') {
    super(code)
    this.code = code
    this.status = status
    this.attemptsLeft = attemptsLeft
    this.email = email
  }
}

/** Everything the console asks of the account host. */
export interface ConsoleApi {
  session(): Promise<SessionState>
  startSignIn(csrf: string, email: string): Promise<SessionState>
  verifySignIn(csrf: string, code: string): Promise<SessionState>
  resendSignIn(csrf: string): Promise<SessionState>
  signOut(csrf: string): Promise<void>
  shares(): Promise<ShareList>
  stopShare(csrf: string, share: string): Promise<void>
  devices(): Promise<DeviceList>
  signOutDevice(csrf: string, id: string): Promise<void>
  browsers(): Promise<BrowserList>
  signOutBrowser(csrf: string, id: string): Promise<void>
  regions(): Promise<RegionList>
  /** One vote for a region; a second for the same region changes nothing. Answers with the leaderboard as cached. */
  voteRegion(csrf: string, region: string): Promise<RegionList>
}
